/**
 * @file SessionRowActions.tsx
 * @brief Row-end "…" menu for a session list row — mirrors the
 *        long-press action sheet on the mobile Sessions card.
 *
 * @details
 * Mobile parity reference: `FRONTEND/src/screens/Sessions/SessionsScreen.tsx`
 * exposes a `VZModal` action sheet on long press with:
 *   1. "Select Multiple"   — N/A on web (no multi-select mode yet).
 *   2. "Delete Session"    — opens a sub-sheet with three flavours:
 *       a. Delete All Data            → wipe row + entire GCS folder
 *       b. Delete Audios Only         → wipe audio assets, keep PDFs
 *       c. Delete Invoice & Transcript→ wipe PDFs, keep audio + invoice row
 *
 * The web equivalent is a Radix `<DropdownMenu>` with the three
 * destructive variants, each guarded by an `<AlertDialog>` confirmation
 * (no rage-clicks). Stops row-click propagation so opening the menu
 * never accidentally also opens the row's detail dialog.
 *
 * Hooks:
 *   useDeleteSession            → all data
 *   useDeleteSessionAudio       → audio only
 *   useDeleteSessionArtifacts   → PDFs only
 *
 * @dependencies
 * - features/sessions/api (useDeleteSession*)
 * - components/ui/{DropdownMenu, AlertDialog, Button}
 *
 * @references
 * - BACKEND/src/features/admin/routes.js (DELETE /api/admin/sessions/:id*)
 * - FRONTEND/src/screens/Sessions/SessionsScreen.tsx (mobile parity)
 *
 * @author DES
 * @date 26-05-2026
 */

import { useState } from "react";
import {
  AudioLines,
  Ellipsis,
  Eye,
  FileText,
  LoaderCircle,
  Trash2,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { deferAfterDropdownMenu } from "@/shared/utils/restoreBodyInteraction";
import {
  useDeleteSession,
  useDeleteSessionArtifacts,
  useDeleteSessionAudio,
} from "../api";
import {
  useDeleteCompanySession,
  useDeleteCompanySessionArtifacts,
  useDeleteCompanySessionAudio,
} from "@/features/company-sessions/api";

export type SessionActionsScope = "platform" | "company";

// ----------------------------------------------------------------------
// Confirmation dialog config — one entry per destructive flavour. The
// ALL flavour stays last so the visually most dangerous option lives
// at the bottom of the menu (matches the mobile sub-sheet ordering).
// ----------------------------------------------------------------------
type Variant = "audio" | "artifacts" | "all";

const VARIANT_COPY: Record<
  Variant,
  {
    title: string;
    description: string;
    action: string;
    successTitle: string;
  }
> = {
  audio: {
    title: "Delete audio?",
    description:
      "This removes the audio files from storage and clears each utterance's audio reference. Transcript, invoice, and metadata stay intact.",
    action: "Delete audio",
    successTitle: "Audio deleted",
  },
  artifacts: {
    title: "Delete invoice & transcript?",
    description:
      "This removes the transcript and invoice PDFs from storage and clears their references. The invoice database row is preserved so revenue stats stay accurate.",
    action: "Delete artifacts",
    successTitle: "Artifacts deleted",
  },
  all: {
    title: "Delete all session data?",
    description:
      "This permanently deletes audio files, transcript, invoice PDF, and marks the session as deleted. The invoice record is kept so revenue stats stay accurate. This cannot be undone.",
    action: "Delete everything",
    successTitle: "Session deleted",
  },
};

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
interface SessionRowActionsProps {
  sessionId: string;
  scope?: SessionActionsScope;
  /** Optional callback fired after a successful "all" delete so the
   *  parent can close any open detail dialog tied to this id. */
  onAllDeleted?: (id: string) => void;
  /** Open the read-only detail dialog. Hooked from `SessionsPage`
   *  so the menu can offer a "View" entry that mirrors a row click —
   *  useful when the entire row is filled by the menu trigger zone. */
  onView?: (id: string) => void;
}

export function SessionRowActions({
  sessionId,
  scope = "platform",
  onAllDeleted,
  onView,
}: SessionRowActionsProps) {
  const [variant, setVariant] = useState<Variant | null>(null);
  const deleteAllPlatform = useDeleteSession();
  const deleteAudioPlatform = useDeleteSessionAudio();
  const deleteArtifactsPlatform = useDeleteSessionArtifacts();
  const deleteAllCompany = useDeleteCompanySession();
  const deleteAudioCompany = useDeleteCompanySessionAudio();
  const deleteArtifactsCompany = useDeleteCompanySessionArtifacts();

  const deleteAll = scope === "company" ? deleteAllCompany : deleteAllPlatform;
  const deleteAudio =
    scope === "company" ? deleteAudioCompany : deleteAudioPlatform;
  const deleteArtifacts =
    scope === "company" ? deleteArtifactsCompany : deleteArtifactsPlatform;

  const mutation =
    variant === "all"
      ? deleteAll
      : variant === "audio"
        ? deleteAudio
        : variant === "artifacts"
          ? deleteArtifacts
          : null;

  const copy = variant ? VARIANT_COPY[variant] : null;

  const handleConfirm = async () => {
    if (!variant || !mutation) return;
    try {
      await mutation.mutateAsync(sessionId);
      toast({
        variant: "success",
        title: VARIANT_COPY[variant].successTitle,
      });
      if (variant === "all") onAllDeleted?.(sessionId);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    } finally {
      setVariant(null);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Session actions"
            // Stop the row's onRowClick from firing when the user
            // taps the menu trigger or anywhere inside the menu.
            onClick={(e) => e.stopPropagation()}
            className="h-8 w-8 rounded-full"
          >
            <Ellipsis className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onClick={(e) => e.stopPropagation()}
          className="w-60"
        >
          {onView ? (
            <>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  onView(sessionId);
                }}
              >
                <Eye className="mr-2 h-4 w-4" />
                View details
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          ) : null}

          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Delete options
          </DropdownMenuLabel>

          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              deferAfterDropdownMenu(() => setVariant("audio"));
            }}
          >
            <AudioLines className="mr-2 h-4 w-4" />
            Delete audios only
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              deferAfterDropdownMenu(() => setVariant("artifacts"));
            }}
          >
            <FileText className="mr-2 h-4 w-4" />
            Delete invoice & transcript
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              deferAfterDropdownMenu(() => setVariant("all"));
            }}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete all data
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={variant !== null}
        onOpenChange={(open) => {
          if (!open) setVariant(null);
        }}
      >
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy?.title}</AlertDialogTitle>
            <AlertDialogDescription>{copy?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation?.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={mutation?.isPending}
              className={cn(
                "bg-destructive text-destructive-foreground hover:bg-destructive/90",
              )}
            >
              {mutation?.isPending ? (
                <>
                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                copy?.action
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
